import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { DraftFunction } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import InsuranceCoverage from '@r6digital/rapidstor-types/types/InsuranceCoverage';

import { WizardValidationError } from '@/types/Wizard';
import { formatCurrency } from '@/utils/currency';
import { RapidStorSettings } from '@/utils/rapidstor';

import CircleCheckbox from '@/components/elements/circle-checkbox';
import ErrorTag from '@/components/elements/error-tag';

import { computedInsurancePremium } from '@/features/rapidstor/helpers';

import classes from './styles.module.scss';

interface Props {
    selectedCoverage: number;
    formErrors: WizardValidationError[];
    coverages: InsuranceCoverage[];
    handleValueChange: (value: string) => void;
    form: Record<string, string>;
    update: (arg: Record<string, string> | DraftFunction<Record<string, string>>) => void;
    taxRate: number;
}

export default function Boxes({
    selectedCoverage,
    formErrors,
    coverages,
    handleValueChange,
    form,
    update,
    taxRate
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const insurancePremium = computedInsurancePremium(coverages, selectedCoverage, taxRate);

    return (
        <>
            {formErrors?.map((error) => (
                <ErrorTag key={`${error.name}_error_idx`} error={error} />
            ))}

            <div className={`${classes.insurance_boxes}`}>
                <div className={`${classes.box}`}>
                    <div className={`${classes.left}`}>
                        <div className={`${classes.first}`}>
                            <input
                                type='checkbox'
                                className={`${classes.checkbox}`}
                                checked={form.insuranceType === 'flatRate'}
                                onChange={() => {
                                    update((draft) => {
                                        draft.insuranceType = 'flatRate';
                                    });
                                }}
                            />
                        </div>
                        <div className={`${classes.second}`}>
                            <span className={`${classes.header}`}>
                                {t('wizard:insuranceCoverage:flatRateInsurance')}
                            </span>
                            <span className={`${classes.subtitle}`}>
                                {t('wizard:insuranceCoverage:usSupply')}
                            </span>
                        </div>
                    </div>
                    <div className={`${classes.right}`}>
                        {coverages[selectedCoverage] ? (
                            <>
                                <div className={`${classes.selectedCoverage}`}>
                                    <span className={`${classes.bold}`}>
                                        {formatCurrency(
                                            insurancePremium,
                                            location,
                                            true,
                                            false
                                        )}{' '}
                                        ={' '}
                                    </span>
                                    <span className={`${classes.normal}`}>
                                        {formatCurrency(
                                            coverages[selectedCoverage].dcCoverage,
                                            location,
                                            false,
                                            true
                                        )}
                                    </span>
                                </div>
                                <span className={`${classes.subtitle}`}>
                                    {t('wizard:insuranceCoverage:for_up_to')}
                                    {formatCurrency(
                                        coverages[selectedCoverage].dcCoverage,
                                        location,
                                        false,
                                        true
                                    )}{' '}
                                    {t('wizard:insuranceCoverage:coverage')}
                                </span>
                            </>
                        ) : (
                            <span className={`${classes.subtitle}`}>
                                {t('wizard:insuranceCoverage:noInsuranceSelected')}
                            </span>
                        )}
                    </div>
                </div>

                <div className={`${classes.box}`}>
                    <div className={`${classes.left}`}>
                        <div className={`${classes.first}`}>
                            <input
                                type='checkbox'
                                className={`${classes.checkbox}`}
                                checked={form.insuranceType === 'self'}
                                onChange={() => {
                                    update((draft) => {
                                        draft.insuranceType = 'self';
                                    });
                                }}
                            />
                        </div>
                        <div className={`${classes.second}`}>
                            <span className={`${classes.header}`}>
                                {t('wizard:insuranceCoverage:selfInsurance')}
                            </span>
                            <span className={`${classes.subtitle}`}>
                                {t('wizard:insuranceCoverage:subTitle')}
                            </span>
                            <span className={`${classes.disclaimer}`}>
                                {t('wizard:insuranceCoverage:proofDisclaimer')}
                            </span>
                        </div>
                    </div>
                    <div className={`${classes.right}`}>
                        <span className={`${classes.subtitle}`}>
                            {t('wizard:insuranceCoverage:fileTypePlease')}
                        </span>
                    </div>
                </div>
            </div>

            {RapidStorSettings(location).insurance().manualEntry().show() && (
                <div className={`${classes.manualEntry}`}>
                    <span className={`${classes.label}`}>
                        {t('wizard:insuranceCoverage:manualEntryLabel')}
                    </span>

                    <div className={`${classes.input_group}`}>
                        <span className={`${classes.prefix}`}>
                            {location.rapidstor.currencySymbol}
                        </span>
                        <input
                            type='number'
                            min='0'
                            value={form.declaredValueOfGoods}
                            onChange={(e) => {
                                handleValueChange(e.target.value);
                                update((draft) => {
                                    draft.declaredValueOfGoods = e.target.value;
                                });
                            }}
                            className={`${classes.input}`}
                        />
                    </div>
                </div>
            )}
            <div style={{ paddingTop: '12px' }}></div>
            {(RapidStorSettings(location).insurance().required() || form.insuranceCoverage !== '' && location.rapidstor.wizards.moveinSingle.insuranceAcceptanceText) && (
                <CircleCheckbox
                    formKey={'declaredValueOfGoodsAgree'}
                    value={form.declaredValueOfGoodsAgree}
                    label={
                        location.rapidstor.wizards.moveinSingle.insuranceAcceptanceText
                    }
                    update={update}
                />
            )}

            {(RapidStorSettings(location).insurance().optional() && form.insuranceCoverage === '' && location.rapidstor.wizards.moveinSingle.insuranceRefusedText) && (
                <CircleCheckbox
                    formKey={'declaredValueOfGoodsAgree'}
                    value={form.declaredValueOfGoodsAgree}
                    label={
                        location.rapidstor.wizards.moveinSingle.insuranceRefusedText
                    }
                    update={update}
                />
            )}
        </>
    );
}
